import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ANEP_CLIENT_ID,
  ConfigTemplate,
  ConfigTemplateAssignResult,
  ConfigTemplateUpdate,
  CustomField,
  DeviceType,
  JsonConfig,
  isAnepTemplate,
} from '../../../types/configTemplate';
import { Client } from '../../../types/client';
import { apiClient } from '../../../services/apiClient';
import { clientService } from '../../../services/clientService';
import { useI18n } from '../../../contexts/I18nContext';
import { useClientAccess } from '../../../hooks/useClientAccess';
import { useJsonConfigVisible } from '../../../hooks/useJsonConfigVisible';
import { findExistingDefault, ExistingDefault } from '../defaultConflict';
import { FieldErrors, FieldValues, configToFieldValues, fieldValuesToJsonConfig, validateFieldFormats, validateTemplateName } from '../templateSchema';
import { useTemplateSchema } from '../useTemplateSchema';

export type TemplateDetailTab = 'overview' | 'config' | 'json' | 'history';

/** A save held back until the user decides what to do with the current default. */
interface DefaultConflict {
  existing: ExistingDefault;
  next: (withDefault: boolean) => void | Promise<void>;
}

/**
 * Everything the template dialog shows and does: the template, its edit
 * form, the gateway schema, the save (with the "replace the default?"
 * guard), the impact preview and the bulk assignment.
 */
export const useTemplateDetail = (templateId: string | null, isOpen: boolean, onTemplateUpdated: () => void) => {
  const { t } = useI18n();
  const { canViewAuditLog, isSuperAdmin, isAdmin } = useClientAccess();
  const showJsonTab = useJsonConfigVisible() === true;
  const canRevealPassword = isSuperAdmin || isAdmin;
  const axios = useMemo(() => apiClient.getAxiosInstance(), []);

  // The template and the dialog
  const [template, setTemplate] = useState<ConfigTemplate | null>(null);
  const [activeTab, setActiveTab] = useState<TemplateDetailTab>('overview');
  const [isEditMode, setIsEditMode] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [defaultConflict, setDefaultConflict] = useState<DefaultConflict | null>(null);
  const [clients, setClients] = useState<Client[]>([]);

  // The edit form
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [ownerId, setOwnerId] = useState<string>(ANEP_CLIENT_ID);
  const [deviceType, setDeviceType] = useState<DeviceType>('');
  const [isDefault, setIsDefault] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [errors, setErrors] = useState<FieldErrors>({});

  // The configuration values, against the schema of the selected gateway type
  const { schema, schemaLoading, productConfig, groupedSchema, passwordInSchema } = useTemplateSchema(deviceType, isOpen);
  const [fieldValues, setFieldValues] = useState<FieldValues>({});
  const [password, setPassword] = useState('');
  const [generatePassword, setGeneratePassword] = useState(false);
  const [customFields, setCustomFields] = useState<CustomField[]>([]);

  const seedFormFromTemplate = useCallback((tpl: ConfigTemplate) => {
    setName(tpl.name);
    setDescription(tpl.description || '');
    setOwnerId(tpl.client_id);
    setDeviceType(tpl.device_type);
    setIsDefault(tpl.is_default);
    setIsActive(tpl.is_active);
    const config = tpl.json_config || {};
    setFieldValues(configToFieldValues(config));
    setPassword('');
    setGeneratePassword(config.generate_password === true);
    setCustomFields((config.custom_fields || []).map((f: CustomField) => ({ ...f, source: 'template' as const })));
  }, []);

  const loadTemplate = useCallback(async () => {
    if (!templateId) return;
    setIsFetching(true);
    setError(null);
    try {
      const { data } = await axios.get(`/v2/config-templates/${templateId}`);
      const loaded: ConfigTemplate = {
        id: String(data.id),
        name: data.name,
        description: data.description,
        client_id: data.client_id || ANEP_CLIENT_ID,
        device_type: data.device_type,
        is_default: data.is_default === true,
        device_count: data.device_count ?? 0,
        json_config: data.json_config || {},
        is_active: data.is_active,
        created_at: data.created_at,
        updated_at: data.updated_at,
      };
      setTemplate(loaded);
      seedFormFromTemplate(loaded);
    } catch (err: any) {
      console.error('[CONFIG TEMPLATE DETAIL] Error fetching template:', err);
      setError(err.response?.data?.detail || err.message || t('configTemplates.detail.loadFailed'));
    } finally {
      setIsFetching(false);
    }
  }, [axios, templateId, seedFormFromTemplate, t]);

  // Open: load the template and the client names.
  useEffect(() => {
    if (!isOpen || !templateId) return;
    loadTemplate();
  }, [isOpen, templateId, loadTemplate]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    clientService.fetchAllClients()
      .then((list) => { if (!cancelled) setClients(list); })
      .catch((err) => { if (!cancelled) console.error('[CONFIG TEMPLATE DETAIL] fetch clients:', err); });
    return () => { cancelled = true; };
  }, [isOpen]);

  // Close: forget the dialog state.
  useEffect(() => {
    if (isOpen) return;
    setIsEditMode(false);
    setShowAssign(false);
    setShowPreview(false);
    setDefaultConflict(null);
    setError(null);
    setSuccessMessage(null);
    setErrors({});
    setActiveTab('overview');
    setTemplate(null);
  }, [isOpen]);

  const setFieldValue = useCallback((fieldName: string, value: string) => {
    setFieldValues((prev) => ({ ...prev, [fieldName]: value }));
    setErrors((prev) => (prev[fieldName] ? { ...prev, [fieldName]: undefined } : prev));
  }, []);

  const ownerName = useCallback((clientId: string): string => {
    if (isAnepTemplate(clientId)) return t('configTemplates.anep');
    return clients.find((c) => c.id === clientId)?.name || clientId;
  }, [clients, t]);

  const valueCount = useMemo(() => {
    const cfg = template?.json_config || {};
    return Object.keys(cfg).filter((k) => k !== 'custom_fields').length + customFields.length;
  }, [template, customFields]);

  const validate = (): boolean => {
    const next: FieldErrors = { ...validateFieldFormats(schema, fieldValues, t) };
    const nameError = validateTemplateName(name, t);
    if (nameError) next.name = nameError;
    if (!ownerId) next.client_id = t('configTemplates.ownerRequired');
    setErrors(next);
    return Object.values(next).every((v) => !v);
  };

  /** The PATCH body from the form; `withDefault` overrides the switch for the conflict dialog. */
  const buildUpdatePayload = useCallback((withDefault?: boolean): { updateData: ConfigTemplateUpdate; jsonConfig: JsonConfig } => {
    const base: JsonConfig = { ...(template?.json_config || {}) };
    delete (base as Record<string, unknown>).custom_fields;
    const jsonConfig = fieldValuesToJsonConfig(schema, fieldValues, base);
    jsonConfig.generate_password = generatePassword;
    if (generatePassword) delete (jsonConfig as Record<string, unknown>).password;
    else if (password.trim()) jsonConfig.password = password.trim();
    if (customFields.length > 0) jsonConfig.custom_fields = customFields;
    const updateData: ConfigTemplateUpdate = {
      name: name.trim(),
      description: description.trim() || undefined,
      client_id: ownerId,
      device_type: deviceType,
      is_default: withDefault ?? isDefault,
      is_active: isActive,
      json_config: jsonConfig,
    };
    return { updateData, jsonConfig };
  }, [template, schema, fieldValues, generatePassword, password, customFields, name, description, ownerId, deviceType, isDefault, isActive]);

  const persist = async (withDefault?: boolean) => {
    if (!template) return;
    const { updateData } = buildUpdatePayload(withDefault);
    await axios.patch(`/v2/config-templates/${template.id}`, updateData);
    setSuccessMessage(t('configTemplates.detail.updated'));
    setIsEditMode(false);
    onTemplateUpdated();
  };

  const runSave = async (withDefault: boolean) => {
    setIsLoading(true);
    try {
      await persist(withDefault);
    } catch (err: any) {
      console.error('[CONFIG TEMPLATE DETAIL] Error updating template:', err);
      setError(err.response?.data?.detail || err.message || t('configTemplates.detail.updateFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Runs `next` at once, unless the save would take the default flag from
   * another template of the same owner and gateway: the dialog asks first.
   */
  const guardDefault = async (next: (withDefault: boolean) => void | Promise<void>) => {
    const alreadyDefaultHere = Boolean(template?.is_default) && ownerId === template?.client_id && deviceType === template?.device_type;
    if (isDefault && !alreadyDefaultHere) {
      const existing = await findExistingDefault(axios, ownerId, deviceType, template?.id);
      if (existing) {
        setDefaultConflict({ existing, next });
        return;
      }
    }
    await next(isDefault);
  };

  const startEdit = () => {
    setIsEditMode(true);
    setSuccessMessage(null);
  };

  const cancelEdit = () => {
    if (template) seedFormFromTemplate(template);
    setIsEditMode(false);
    setError(null);
    setErrors({});
  };

  const save = async () => {
    if (!template) return;
    setError(null);
    setSuccessMessage(null);
    if (!validate()) return;
    await guardDefault(runSave);
  };

  const openPreview = async () => {
    if (!template) return;
    setError(null);
    setSuccessMessage(null);
    if (!validate()) return;
    await guardDefault((withDefault) => {
      if (!withDefault) setIsDefault(false);
      setShowPreview(true);
    });
  };

  const confirmFromPreview = async () => {
    setError(null);
    setIsLoading(true);
    try {
      await persist();
      setShowPreview(false);
    } catch (err: any) {
      console.error('[CONFIG TEMPLATE DETAIL] Error updating template:', err);
      setError(err.response?.data?.detail || err.message || t('configTemplates.detail.updateFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  /** The user's answer to the "replace the default?" dialog; null cancels. */
  const resolveConflict = (withDefault: boolean | null) => {
    const pending = defaultConflict;
    setDefaultConflict(null);
    if (!pending || withDefault === null) return;
    if (!withDefault) setIsDefault(false);
    void pending.next(withDefault);
  };

  const handleAssigned = (result: ConfigTemplateAssignResult) => {
    setSuccessMessage(t('configTemplates.detail.assigned', { count: result.assigned, name: result.template_name }));
    loadTemplate();
  };

  const previewRequest = showPreview && template
    ? {
        template_id: Number(template.id),
        name: name.trim() || template.name,
        device_type: deviceType,
        json_config: buildUpdatePayload().jsonConfig as Record<string, unknown>,
        client_ids: ownerId && !isAnepTemplate(ownerId) ? [ownerId] : null,
      }
    : null;

  return {
    // dialog
    template, isFetching, isLoading, error, successMessage, setSuccessMessage,
    activeTab, setActiveTab, isEditMode, canViewAuditLog, showJsonTab, canRevealPassword,
    // form
    name, setName, description, setDescription, ownerId, setOwnerId, deviceType, setDeviceType,
    isDefault, setIsDefault, isActive, setIsActive, errors, setErrors,
    // configuration
    schema, schemaLoading, productConfig, groupedSchema, passwordInSchema,
    fieldValues, setFieldValue, password, setPassword, generatePassword, setGeneratePassword,
    customFields, setCustomFields, valueCount,
    // actions
    ownerName, startEdit, cancelEdit, save, openPreview, confirmFromPreview,
    showPreview, setShowPreview, previewRequest,
    showAssign, setShowAssign, handleAssigned,
    defaultConflict, resolveConflict,
  };
};

export type TemplateDetailState = ReturnType<typeof useTemplateDetail>;
